/** Real client for `/api/v1/ai/kb` (api/app/ai/kb_routes.py). Same origin + session cookie as the chat API. */
import { ApiError } from '../client'
import { reportUnauthorized } from '../http'
import type { KbAction, KbApi, KbEntity, KbFields, KbKind, KbLogPage, KbState, KbTryResult } from './types'

type Query = Record<string, string | number | null | undefined>

export class HttpKbApi implements KbApi {
  constructor(
    private readonly base: string,
    private readonly timeoutMs = 15000,
    /** the test box waits for the whole pipeline (classifier + tools + draft, server cap 40 s) */
    private readonly askTimeoutMs = 50000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private url(path: string, q?: Query): string {
    const u = new URL(`${this.base.replace(/\/+$/, '')}/api/v1/ai/kb${path}`, window.location.origin)
    for (const [k, v] of Object.entries(q ?? {})) {
      if (v !== null && v !== undefined && v !== '') u.searchParams.set(k, String(v))
    }
    return u.toString()
  }

  private async req<T>(method: string, path: string, opts: { query?: Query; body?: unknown; timeoutMs?: number } = {}): Promise<T> {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? this.timeoutMs)
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
    try {
      const res = await this.fetchImpl(this.url(path, opts.query), {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      if (res.status === 401) reportUnauthorized()
      if (!res.ok) {
        let detail = res.statusText
        try {
          const j = (await res.json()) as { detail?: unknown }
          if (typeof j.detail === 'string') detail = j.detail
        } catch {
          /* body not JSON */
        }
        throw new ApiError(res.status, detail || `HTTP ${res.status}`)
      }
      return (await res.json()) as T
    } catch (e) {
      if (e instanceof ApiError) throw e
      if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'หมดเวลารอเซิร์ฟเวอร์')
      throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้')
    } finally {
      clearTimeout(timer)
    }
  }

  load(): Promise<KbState> {
    return this.req('GET', '')
  }

  create(kind: KbKind, fields: KbFields): Promise<KbState> {
    return this.req('POST', `/${kind}`, { body: fields })
  }

  edit(kind: KbKind, id: string, fields: KbFields): Promise<KbState> {
    return this.req('PATCH', `/${kind}/${encodeURIComponent(id)}`, { body: fields })
  }

  act(kind: KbKind, id: string, action: KbAction, fields?: KbFields): Promise<KbState> {
    return this.req('POST', `/${kind}/${encodeURIComponent(id)}/${action}`, {
      body: fields ? { fields } : undefined,
    })
  }

  log(opts: { entity?: KbEntity | null; before?: string | null } = {}): Promise<KbLogPage> {
    return this.req('GET', '/log', { query: { entity: opts.entity, before: opts.before } })
  }

  undo(logId: string): Promise<KbState> {
    return this.req('POST', `/log/${encodeURIComponent(logId)}/undo`)
  }

  ask(question: string): Promise<KbTryResult> {
    return this.req('POST', '/try', { body: { question }, timeoutMs: this.askTimeoutMs })
  }
}
